/**
 * Dynamic medical PA form: renders the configured sections and the attachments
 * block, delegating all state/effects/handlers to {@link useMedicalPaForm}.
 *
 * @module medicalPaForm/components/medicalPaForm
 */
import { LatestMedicalPaFormData } from "../../../api/getCalls/getLatestMedicalPaFormData";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModal";
import { FormDataModel } from "../../../data-model/pharmaPaFormModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import useMedicalPaForm from "../hooks/useMedicalPaForm";
import MedicalPaFormAttachments from "./medicalPaFormAttachments";
import MedicalPaFormSection from "./medicalPaFormSection";

/** Props for {@link MedicalPaForm}. */
interface MedicalPaFormProps {
  sectionOnClick?: (sectionKey: string) => void;
  docId: string;
  scrollSectionId?: (sectionKey: string) => void;
  formConfiguration?: FormDataModel;
  latestMedicalPaFormData?: LatestMedicalPaFormData;
}

/**
 * Renders the configurable medical PA form once its configuration and field
 * values resolve.
 *
 * @param props - See {@link MedicalPaFormProps}.
 * @returns The form sections, attachments, and clinical-document viewer.
 */
const MedicalPaForm = (props: MedicalPaFormProps) => {
  const { sectionOnClick, scrollSectionId } = props;

  const {
    formConfig,
    formFieldsData,
    localMedicalPaOrder,
    originalMedicalPaOrder,
    clinicalDocumentFilePath,
    isDocViewerOpen,
    onAddMore,
    onDelete,
    openClinicalDocument,
    closeClinicalDocument,
  } = useMedicalPaForm({
    formConfiguration: props.formConfiguration,
    latestMedicalPaFormData: props.latestMedicalPaFormData,
    scrollSectionId,
  });

  return (
    <>
      {formConfig &&
        Object.keys(formConfig).length > 0 &&
        formFieldsData &&
        Object.keys(formFieldsData).length > 0 && (
          <div className="pharma-form__container">
            {formConfig?.fields
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((section) => (
                <MedicalPaFormSection
                  key={section.key}
                  section={section}
                  onSectionClick={sectionOnClick}
                  data={localMedicalPaOrder}
                  originalData={originalMedicalPaOrder}
                  onAddMore={onAddMore}
                  onDelete={onDelete}
                />
              ))}
            <MedicalPaFormAttachments
              clinicalDocumentFilePath={clinicalDocumentFilePath}
              onSectionClick={sectionOnClick}
              onViewDocument={openClinicalDocument}
            />
          </div>
        )}
      {isDocViewerOpen && (
        <DocViewerModal
          onClose={closeClinicalDocument}
          type={"clinical_attachment"}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.FORM_SCREEN}
          formName={formConfig?.formTitle ?? ""}
        />
      )}
    </>
  );
};

export default MedicalPaForm;
